import { Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import LanguageRouter from './components/LanguageRouter';
import FallbackRouter from './components/FallbackRouter';
import RoutingErrorBoundary from './components/RoutingErrorBoundary';
import { ReCaptchaProvider } from './components/ReCaptchaProvider';
import { AuthProvider } from './contexts/AuthContext';
import { useI18nWithFallback } from './hooks/useI18nWithFallback';
import './i18n';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a365d', // Navy blue
    },
    secondary: {
      main: '#4299e1', // Light blue
    },
    info: {
      main: '#2196F3', // Standard blue
      light: '#E3F2FD', // Very light blue for better readability
      dark: '#1565C0',
    },
    success: {
      main: '#4CAF50', // Standard green
      light: '#E8F5E8', // Very light green for better readability  
      dark: '#2E7D32',
    },
    background: {
      default: '#f7fafc', // Very light gray
      paper: '#ffffff',
    },
    text: {
      primary: '#1a365d',
      secondary: '#4a5568',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
});

function App() {
  const i18nState = useI18nWithFallback();

  // Show loading while i18n is initializing (with timeout)
  if (!i18nState.isReady) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Cargando aplicación... / Loading application...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // Show error state with fallback option
  if (i18nState.hasError && !i18nState.useFallback) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}>
          <Alert severity="error" sx={{ mb: 2, maxWidth: '600px' }}>
            <Typography variant="h6" gutterBottom>
              Error de Carga / Loading Error
            </Typography>
            <Typography variant="body2">
              No se pudieron cargar las traducciones. La aplicación funcionará en modo básico.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Could not load translations. The application will work in basic mode.
            </Typography>
          </Alert>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" gutterBottom>
              Redirigiendo al modo básico... / Redirecting to basic mode...
            </Typography>
            <CircularProgress size={24} />
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  const RouterComponent = i18nState.useFallback ? FallbackRouter : LanguageRouter;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ReCaptchaProvider>
          <AuthProvider>
            <RoutingErrorBoundary
            fallbackRoutes={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                <a href="/es/" style={{ textDecoration: 'none', color: '#1976d2' }}>
                  Inicio / Home
                </a>
                <a href="/es/registrarse" style={{ textDecoration: 'none', color: '#1976d2' }}>
                  Registrarse / Register
                </a>
                <a href="/es/como-funciona" style={{ textDecoration: 'none', color: '#1976d2' }}>
                  Cómo Funciona / How It Works
                </a>
              </Box>
            }
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Navigation />
              <Box component="main" sx={{ flex: 1 }}>
                <Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress />
                  </Box>
                }>
                  <RouterComponent />
                </Suspense>
              </Box>
              <Footer />
            </Box>
            </RoutingErrorBoundary>
          </AuthProvider>
        </ReCaptchaProvider>
      </Router>
    </ThemeProvider>
  );
}
export default App;
